import React, { type ReactNode, type Ref } from 'react';

import memoize from 'lodash/memoize';
import { Box, xcss } from '@atlaskit/primitives';
import InformationIcon from '@atlaskit/icon/core/information';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { HoverPopover, CellHoverRenderer } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../../controllers/features/selectors.tsx';

/**
 * We memoize this event by the **first** argument, e.g. fieldType, as we only really care about the proportion of
 * users encountering this scenario. We don't need to send an event each time this component is rendered.
 */
export const fireTrackAnalyticsOncePerFieldType = memoize(
	(fieldType: string, numberOfElements: number, createAnalyticsEvent) => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'tableCell multipleElementsRendered', {
			fieldType,
			numberOfElements,
		});
	},
);

type Props = {
	/**
	 * Table cell element to render for an issue field.
	 */
	cellElements: ReactNode[];
	/**
	 * Flag to enable min-height containers around each individual cell element as a stop-gap solution to fix layout
	 * issues with duplicate inline editable fields (which have built-in `min-height: 100%`).
	 * TODO JSC-1783 Handle duplicate field layouts within single row height
	 */
	multipleFieldLayoutAdjustment?: boolean;
	/**
	 * Type of the issue field.
	 */
	type: string;
};

export const MultipleCellElements = ({
	cellElements,
	multipleFieldLayoutAdjustment = false,
	type,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	fireTrackAnalyticsOncePerFieldType(type, cellElements.length, createAnalyticsEvent);
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	if (isSingleLineRowHeightEnabled && cellElements.length > 0) {
		const firstElement = cellElements[0];

		return (
			<Box xcss={rowStyle}>
				<CellHoverProvider>
					<CellHoverRenderer shouldFillHeight>{firstElement}</CellHoverRenderer>
				</CellHoverProvider>
				<CellHoverProvider>
					<CellHoverRenderer>
						<HoverPopover content="Content goes here" isInteractive>
							{({ ref }) => (
								<IconButton
									label=""
									appearance="subtle"
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									ref={ref as Ref<HTMLButtonElement>}
									icon={(iconProps) => (
										<InformationIcon {...iconProps} color={token('color.icon.information')} />
									)}
									testId="native-issue-table.ui.row.issue-row.cell.multiple-cell-elements.issue-table"
								/>
							)}
						</HoverPopover>
					</CellHoverRenderer>
				</CellHoverProvider>
			</Box>
		);
	}

	return multipleFieldLayoutAdjustment ? (
		<Box xcss={containerStyles}>
			{cellElements.map((cellElement, index) => (
				<Box key={index} xcss={elementContainerStyles}>
					{cellElement}
				</Box>
			))}
		</Box>
	) : (
		cellElements
	);
};

const rowStyle = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 'space.050',
	height: '100%',
});

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	gap: 'space.050',
});

const elementContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: '32px',
});
